import { Badge } from '@/components/ui/badge';
import { Check, Minus, X, User, MessageSquare } from 'lucide-react';
import { m } from 'motion/react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  fadeInUp,
  staggerContainer,
  staggerChild,
  defaultViewport,
  getReducedMotionVariants,
} from '@/lib/motion';
import { useTranslation } from '@/i18n/i18n';
import { AiAccountingLink } from '@/components/AiAccountingLink';
import { AI_ACCOUNTING_ANCHORS } from '@/content/ai-accounting-anchors';

type RowStatus = 'yes' | 'no' | 'partial';

interface ComparisonRow {
  label: string;
  accountant: RowStatus;
  chatgpt: RowStatus;
  bookeper: RowStatus;
}

// Dowód check treatment (DESIGN.md): plain marks, no colored circle chips —
// one check dialect across the whole site.
function StatusIcon({ status, label }: { status: RowStatus; label: string }) {
  if (status === 'yes') {
    return (
      <span role="img" aria-label={label} className="flex-shrink-0">
        <Check aria-hidden="true" className="size-4 text-success" />
      </span>
    );
  }
  if (status === 'partial') {
    return (
      <span role="img" aria-label={label} className="flex-shrink-0">
        <Minus aria-hidden="true" className="size-4 text-amber-600 dark:text-amber-400" />
      </span>
    );
  }
  return (
    <span role="img" aria-label={label} className="flex-shrink-0">
      <X aria-hidden="true" className="size-4 text-muted-foreground/50" />
    </span>
  );
}

export const SolutionSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const { t, locale } = useTranslation();

  const comparisonRows: ComparisonRow[] = [
    { label: t('solution.row.correctCalc'), accountant: 'yes', chatgpt: 'no', bookeper: 'yes' },
    { label: t('solution.row.instantReply'), accountant: 'no', chatgpt: 'yes', bookeper: 'yes' },
    { label: t('solution.row.submission'), accountant: 'yes', chatgpt: 'no', bookeper: 'yes' },
    { label: t('solution.row.ocrMapping'), accountant: 'no', chatgpt: 'no', bookeper: 'yes' },
    { label: t('solution.row.pkdMapping'), accountant: 'partial', chatgpt: 'no', bookeper: 'yes' },
    { label: t('solution.row.auditTrail'), accountant: 'yes', chatgpt: 'no', bookeper: 'yes' },
  ];

  const columns = [
    {
      key: 'accountant' as const,
      title: t('solution.col.accountant'),
      subtitle: t('solution.col.accountantPrice'),
      description: t('solution.col.accountantDesc'),
      icon: User,
      highlighted: false,
    },
    {
      key: 'chatgpt' as const,
      title: t('solution.col.chatgpt'),
      subtitle: t('solution.col.chatgptPrice'),
      description: t('solution.col.chatgptDesc'),
      icon: MessageSquare,
      highlighted: false,
    },
    {
      key: 'bookeper' as const,
      title: t('solution.col.bookeper'),
      subtitle: t('solution.col.bookeperPrice'),
      description: t('solution.col.bookeperDesc'),
      icon: null,
      useMascot: true,
      highlighted: true,
    },
  ];
  const mobileColumns = [...columns].sort((left, right) => {
    const priority = { bookeper: 0, accountant: 1, chatgpt: 2 };
    return (priority[left.key] ?? 3) - (priority[right.key] ?? 3);
  });

  const statusLabel: Record<RowStatus, string> = {
    yes: t('solution.statusYes'),
    partial: t('solution.statusPartial'),
    no: t('solution.statusNo'),
  };

  return (
    <section
      id="comparison"
      aria-labelledby="comparison-heading"
      className="py-14 px-5 sm:px-6 md:px-8 md:py-20 bg-background"
    >
      <div className="max-w-5xl mx-auto">
        {/* Section header */}
        <m.div
          className="mb-8 text-left md:mb-12 md:text-center"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={shouldReduceMotion ? getReducedMotionVariants(fadeInUp) : fadeInUp}
        >
          <Badge className="mb-4" variant="outline">
            {t('solution.badge')}
          </Badge>
          <h2
            id="comparison-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-4 tracking-tight"
          >
            {/* Yellow budget (DESIGN.md): this section's single accent belongs
                to the highlighted Bookeper column, so the heading renders the
                <gradient> markers as plain text — strip them, don't style them. */}
            {t('solution.heading').replace(/<\/?gradient>/g, '')}
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl md:mx-auto md:text-lg">
            {t('solution.description')}
          </p>
          {locale === 'pl' ? (
            <p className="mt-4 text-sm text-muted-foreground">
              <AiAccountingLink anchor={AI_ACCOUNTING_ANCHORS.home} />
            </p>
          ) : null}
        </m.div>

        {/* Mobile comparison: no horizontal discovery required. The winning
            Bookeper row is shown first because users scan, they don't study
            comparison tables on a 320px screen. */}
        <m.div
          className="space-y-3 md:hidden"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={
            shouldReduceMotion ? getReducedMotionVariants(staggerContainer) : staggerContainer
          }
        >
          {comparisonRows.map((row) => (
            <m.div
              key={row.label}
              className="rounded-2xl border border-border bg-card p-4 shadow-sm"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              <p className="text-base font-semibold leading-snug text-foreground">{row.label}</p>
              <div className="mt-3 space-y-2">
                {mobileColumns.map((col) => (
                  <div
                    key={col.key}
                    data-comparison-column={col.key}
                    className={`flex min-h-11 items-center justify-between rounded-xl border px-3 py-2.5 ${
                      col.highlighted
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border bg-background/60 text-muted-foreground'
                    }`}
                  >
                    <div>
                      <span className="block text-sm font-bold text-foreground">{col.title}</span>
                      <span className="block text-xs">{col.subtitle}</span>
                    </div>
                    <StatusIcon status={row[col.key]} label={statusLabel[row[col.key]]} />
                  </div>
                ))}
              </div>
            </m.div>
          ))}
        </m.div>

        {/* Comparison table */}
        <div className="hidden overflow-x-auto md:block">
          <m.div
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={
              shouldReduceMotion ? getReducedMotionVariants(staggerContainer) : staggerContainer
            }
          >
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_repeat(3,140px)] md:grid-cols-[1fr_repeat(3,180px)] gap-3 mb-2">
              <div /> {/* Empty cell for row labels */}
              {columns.map((col) => {
                const Icon = col.icon;
                return (
                  <m.div
                    key={col.key}
                    data-comparison-column={col.key}
                    className={`flex flex-col items-center text-center py-5 px-3 rounded-xl ${
                      col.highlighted
                        ? 'bg-primary/10 border-2 border-primary shadow-lg shadow-primary/10'
                        : 'bg-card border border-border'
                    }`}
                    variants={
                      shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild
                    }
                  >
                    {'useMascot' in col && col.useMascot ? (
                      <div className="size-10 rounded-lg bg-primary/20 flex items-center justify-center mb-2">
                        <img
                          src="/sprites/ghost-calculating-frame.png"
                          alt="Bookeper AI mascot"
                          width={341}
                          height={341}
                          loading="lazy"
                          decoding="async"
                          className="size-7 object-contain"
                        />
                      </div>
                    ) : Icon ? (
                      <div
                        className={`size-10 rounded-lg flex items-center justify-center mb-2 ${
                          col.highlighted ? 'bg-primary/20' : 'bg-muted'
                        }`}
                      >
                        <Icon
                          className={`size-5 ${col.highlighted ? 'text-primary' : 'text-muted-foreground'}`}
                        />
                      </div>
                    ) : null}
                    <span className="text-sm font-bold text-foreground">{col.title}</span>
                    <span
                      className={`text-xs font-semibold mt-0.5 ${col.highlighted ? 'text-foreground' : 'text-muted-foreground'}`}
                    >
                      {col.subtitle}
                    </span>
                    <span className="text-[11px] text-muted-foreground mt-1 hidden md:block">
                      {col.description}
                    </span>
                  </m.div>
                );
              })}
            </div>

            {/* Comparison rows */}
            <div className="space-y-1">
              {comparisonRows.map((row) => (
                <m.div
                  key={row.label}
                  className="grid grid-cols-[1fr_repeat(3,140px)] md:grid-cols-[1fr_repeat(3,180px)] gap-3 items-center border-b border-border/40 last:border-b-0"
                  variants={
                    shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild
                  }
                >
                  <span className="text-sm text-foreground/80 py-3 pl-2">{row.label}</span>
                  {columns.map((col) => (
                    <div
                      key={col.key}
                      data-comparison-column={col.key}
                      className={`flex justify-center py-3 rounded-lg ${
                        col.highlighted ? 'bg-primary/5' : ''
                      }`}
                    >
                      <StatusIcon status={row[col.key]} label={statusLabel[row[col.key]]} />
                    </div>
                  ))}
                </m.div>
              ))}
            </div>
          </m.div>
        </div>

        {/* Bottom trust line */}
        <m.p
          className="text-sm text-muted-foreground text-center mt-10 max-w-2xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={shouldReduceMotion ? getReducedMotionVariants(fadeInUp) : fadeInUp}
        >
          {t('solution.bottomLine')
            .split('<bold>')
            .map((part, i) => {
              if (i === 0) return part;
              const [bold, rest] = part.split('</bold>');
              return (
                <span key={bold}>
                  <span className="font-semibold text-foreground">{bold}</span>
                  {rest}
                </span>
              );
            })}
        </m.p>
      </div>
    </section>
  );
};
